import React, { useEffect, useContext, useState } from 'react'
import { startCase, toLower } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import { Grid } from '@mui/material'
import { clearTransportationInformation } from 'state/inboundTicket'

import { dateUnixFormatter, roundTo } from 'utils/data-manipulation/valueFormatter'
import BillsGrid from 'components/containers/bills/components/BillsGrid'
import { useGetBillsQuery } from 'services/bills'
import {
  BILL_STATUSES,
  CURRENCY_PREPENDS,
  BILL_TYPES,
  SORTABLE_BILL_FIELDS,
  ROUNDING_PRECISIONS,
  PAYMENT_METHODS_VALUES,
} from 'utils/constants'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { FREIGHT_ACTIONS } from 'utils/ui/actions'

const FreightBillsContainer = () => {
  const dispatch = useDispatch()
  const context = useContext(ContentLayoutContext)

  const [filter, setFilter] = useState({
    sort: {
      field: SORTABLE_BILL_FIELDS.EXTERNAL_ID,
      ascending: false,
    },
    type: [BILL_TYPES.FREIGHT.id],

    // Retrieve only multi-ticket bills
    filterSingleTicket: true,
    search: '',
  })

  const { data, isFetching } = useGetBillsQuery({
    filter: JSON.stringify(filter),
  })

  useGlobalLoader(false)

  const { defaultCurrency } = useSelector(state => state.settings)
  const { currencyExchangeRatesFF } = useSelector(state => state.featureFlags?.variables || {})
  const [rows, setRows] = useState([])

  useEffect(() => {
    context.setContainerTitle('Freight Bills')
    context.setContainerActions(FREIGHT_ACTIONS.filter(action => action.id === 'add-freight-bill-button'))
    dispatch(clearTransportationInformation())
  }, [])

  useEffect(() => {
    const transformedRows = (data?.Items || [])
      .slice()
      .filter(bill => bill.type === BILL_TYPES.FREIGHT.id)
      .map(bill => {
        const billSubtotal = (bill?.netCost || 0) + parseFloat(bill?.appliedRoundingDelta || 0)
        const billTax = roundTo((billSubtotal * parseFloat(bill?.taxRate || 0)) / 100, ROUNDING_PRECISIONS.COST)
        const billCurrency = bill?.currency
        const billPricePrepend =
          !billCurrency || defaultCurrency === billCurrency
            ? CURRENCY_PREPENDS[defaultCurrency]?.hc
            : CURRENCY_PREPENDS[billCurrency]?.fc
        return {
          ...bill,
          billNumber: bill?.externalId,
          billLink: `/app/freight/freight-bills/${bill.id}`,
          billStatus: BILL_STATUSES[bill?.status?.toUpperCase()]?.name || startCase(toLower(bill?.status || '')),
          type: BILL_TYPES[bill?.type]?.name || startCase(toLower(bill?.type || '')),
          customerName: bill?.customerDisplayName || '',
          customerId: bill?.customerId,
          customerPreferredPaymentMethod: PAYMENT_METHODS_VALUES[bill?.customerPreferredPaymentMethod] || '',
          ticketCount: bill?.numberOfTickets || 0,
          billTotal: billSubtotal + billTax,
          paid: bill?.total || 0,
          lastUpdated: dateUnixFormatter(bill?.updatedAt),
          pricePrepend: currencyExchangeRatesFF ? billPricePrepend : '$',
        }
      })
    setRows(transformedRows)
  }, [data])

  return (
    <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
      <BillsGrid rows={rows} loading={isFetching} filter={filter} setFilter={setFilter} />
    </Grid>
  )
}

export default FreightBillsContainer
